import React, { FC, useCallback, useContext, useMemo } from 'react';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { UrlQueryDataGrid } from '../components/UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowParams } from '@mui/x-data-grid';
import { getDateValueFormatter } from '../valueFormatters/DateValueFormatter';
import { DateFormat } from '../helpers/dateFormat';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { getStringEqualsOperator } from '../filterOperators/stringEqualsOperator';
import { getStringDiffersOperator } from '../filterOperators/stringDiffersOperator';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../filterOperators/dateFilterOperators';
import { getBoolFilterOperator } from '../filterOperators/boolFilterOperator';
import { OrganizationsFetcher } from '../fetchers/OrganizationsFetcher';
import { EasyFilter } from '../components/EasyFilter/EasyFilter';
import { IQuickFiltersProps } from '../components/EasyFilter/QuickFilters';
import { CellContentTooltip } from '../components/CellContentTooltip/CellContentTooltip';
import { Trans, useTranslation } from 'react-i18next';
import { getStringContainsOperator } from '../filterOperators/stringContainsOperator';
import { useDialog } from '../hooks/useDialog';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { Link, useLocation } from 'react-router-dom';
import { useIsGuardedRouteAllowed } from '../resources/GuardedRouteLink';
import { AddOrganizationDialog } from '../components/EditOrganizationDialog/AddOrganizationDialog';
import { LazyUser } from '../components/LazyData/LazyUser';
import { FilteringContextProvider } from '../contexts/FilteringContext';
import { IServerSorting } from '../contexts/Fetcher';
import { apiOrganization } from '../resources/api/apiOrganization';
import { useQueryParam } from '../hooks/useQueryParam';
import { ConfirmationDialog } from '../components/ConfirmationDialog/ConfirmationDialog';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useCloseDialog } from '../hooks/useCloseDialog';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { DangerousButton } from '../components/CustomButtons/DangerousButton';
import { queryUrl } from '../hooks/useQueryUrl';
import { NotificationContext } from '../contexts/NotificationContext';

const { useUnArchiveOrganizationByName, useDeleteArchivedOrganization } = apiOrganization;

const UnarchiveBtn: FC<{ name: string }> = ({ name }) => {
  const { Guard } = useUnArchiveOrganizationByName(name);
  const { openUrl } = useDialog(name, 'unarchive');
  const { t } = useTranslation();

  return (
    <Guard>
      <Button component={Link} to={openUrl} size={'small'} variant={'contained'} color={'secondary'} replace={true}>
        {t('common.button.unArchive')}
      </Button>
    </Guard>
  );
};

const OrganizationsContent: FC = () => {
  const { data, onRefresh } = useContext(OrganizationsFetcher.Context);
  const { t } = useTranslation();
  const handleError = useApiErrorHandler();
  const { search } = useLocation();
  const { addNotification } = useContext(NotificationContext);

  const unarchiveOrgName = useQueryParam('unarchive');
  const deleteOrgName = useQueryParam('delete');
  const unarchiveOrg = data?.payload.data.find((org) => org.name === unarchiveOrgName);
  const deleteOrg = data?.payload.data.find((org) => org.name === deleteOrgName);

  const { execute: unArchive } = useUnArchiveOrganizationByName(unarchiveOrgName ?? void 0);
  const { execute: deleteOrganization, Guard: GuardDelete } = useDeleteArchivedOrganization(
    deleteOrg?.name ?? unarchiveOrg?.name
  );

  const closeDialog = useCloseDialog(void 0, ['unarchive', 'delete']);

  const onUnArchive = useCallback(async () => {
    try {
      await unArchive();
      await onRefresh();
      addNotification({
        severity: 'success',
        message: t('organization.archiveDialog.successUnarchive'),
      });
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [unArchive]);

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'name',
        headerName: t('common.tableHeader.organization'),
        flex: 1,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator(), getStringDiffersOperator()],
        renderCell: (params) => (
          <CellContentTooltip>
            {params.row.archived ? (
              <>
                <UnarchiveBtn name={params.value} />{' '}
              </>
            ) : null}
            {params.value}
          </CellContentTooltip>
        ),
      },
      {
        field: 'owner',
        headerName: t('common.tableHeader.owner'),
        flex: 1,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator()],
        renderCell: (params) =>
          params.value ? (
            <CellContentTooltip>
              <LazyUser id={params.value} />
            </CellContentTooltip>
          ) : null,
      },
      {
        field: 'email',
        headerName: t('common.tableHeader.email'),
        flex: 2,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator()],
        renderCell: (params) => {
          if (!params.value) return null;
          const { archived } = params.row;

          const email = <LazyUser id={params.value} Render={({ user }) => <span>{user.email}</span>} />;

          if (archived) {
            const parts = [t('common.accountStatus.archived'), email];
            return (
              <Tooltip title={parts.join(' | ')}>
                <span>{email}</span>
              </Tooltip>
            );
          } else {
            return <CellContentTooltip>{email}</CellContentTooltip>;
          }
        },
      },
      {
        field: 'pixotope_representative',
        headerName: t('organizations.table.representativeHeader'),
        width: 180,
        filterOperators: [getStringContainsOperator(), getStringEqualsOperator()],
        renderCell: (params) =>
          params.value ? (
            <CellContentTooltip>
              <LazyUser id={params.value} />
            </CellContentTooltip>
          ) : null,
      },
      {
        field: 'created_at',
        headerName: t('common.tableHeader.created'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 130,
        filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
      },
      {
        field: 'archived',
        headerName: t('common.tableHeader.archived'),
        hide: true,
        hideable: false,
        filterOperators: [getBoolFilterOperator()],
      },
    ],
    []
  );

  const getRowLink = useCallback((params: GridRowParams) => {
    if (params.row.archived) return null;
    return AuthenticatedRoutes.OrganizationDetails(params.row.name);
  }, []);

  const isGetRowLinkAllowed = useIsGuardedRouteAllowed('OrganizationDetails', ['any']);

  const rows = useMemo(() => {
    if (!data) return null;
    return data.payload.data.map((row) => {
      return {
        id: row.id,
        name: row.name,
        owner: row.owner_id,
        email: row.owner_id,
        pixotope_representative: row.pixotope_representative,
        created_at: row.created_at,
        archived: row.archived,
        original: row,
      };
    });
  }, [data]);

  const onDelete = useCallback(async () => {
    try {
      await deleteOrganization();
      await onRefresh();
      closeDialog();
      addNotification({
        severity: 'success',
        message: t('organization.archiveDialog.successDelete'),
      });
    } catch (e) {
      handleError(e);
    }
  }, [deleteOrganization]);

  if (!rows || !data) return null;

  return (
    <>
      <UrlQueryDataGrid
        columns={columns}
        rows={rows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        paginationMode={'server'}
        filterMode={'server'}
        sortingMode={'server'}
        getRowLink={isGetRowLinkAllowed ? getRowLink : void 0}
        rowCount={data.payload.total_items}
        getRowClassName={(params) => (params.row.archived ? 'Mui-disabled' : '')}
      />
      <Dialog open={!!unarchiveOrg} onClose={closeDialog}>
        <DialogTitle>{t('organization.archiveDialog.titleUnArchive')}</DialogTitle>
        <DialogContent>
          <Trans i18nKey="organization.archiveDialog.contentUnArchive">
            <Typography paragraph />
            <List>
              <ListItem />
              <ListItem />
              <ListItem />
            </List>
            <Typography paragraph />
          </Trans>
        </DialogContent>
        <DialogActions>
          <Button variant={'outlined'} onClick={closeDialog}>
            {t('common.dialog.close')}
          </Button>
          <GuardDelete>
            <DangerousButton
              component={Link}
              to={queryUrl('unarchive', null, queryUrl('delete', unarchiveOrg?.name ?? '', search))}
              replace={true}
            >
              {t('organization.archiveDialog.delete')}
            </DangerousButton>
          </GuardDelete>
          <Button variant={'contained'} onClick={onUnArchive}>
            {t('organization.archiveDialog.confirmUnArchive')}
          </Button>
        </DialogActions>
      </Dialog>
      <GuardDelete>
        <ConfirmationDialog
          open={!!deleteOrg}
          title={t('organization.archiveDialog.deleteTitle')}
          message={t('organization.archiveDialog.deleteMessage', { organization: deleteOrg?.name ?? '' })}
          confirmLabel={t('organization.archiveDialog.deleteConfirm')}
          confirm={onDelete}
          abort={closeDialog}
          dangerous={true}
        />
      </GuardDelete>
    </>
  );
};

const defaultSort: IServerSorting = {
  sortBy: 'created_at',
  sortOrder: 'desc',
};

export const OrganizationsPage: FC = () => {
  const { t } = useTranslation();

  const { isOpen: isNewOpen, openUrl: newUrl, close: abortNew } = useDialog('new');

  const quickFilters = useMemo<IQuickFiltersProps['quickFilters']>(
    () =>
      new Map([
        [
          t('common.quickFilter.archived'),
          {
            filterField: 'archived',
            filterOp: 'equals',
            filterVal: 'true',
          },
        ],
      ]),
    []
  );

  return (
    <FilteringContextProvider quickFilters={quickFilters} defaultSorting={defaultSort}>
      <OrganizationsFetcher.WAF>
        <PageHeader title={t('organizations.title')}>
          <Button component={Link} to={newUrl} variant="contained" color="primary" replace={true}>
            {t('organizations.addButton')}
          </Button>
        </PageHeader>

        <EasyFilter quickFilters={quickFilters} />
        <OrganizationsContent />

        <AddOrganizationDialog open={isNewOpen} closeDialog={abortNew} />
      </OrganizationsFetcher.WAF>
    </FilteringContextProvider>
  );
};
